import React, { useState, useEffect } from 'react';
import { testimonialService } from '../services/testimonialService';
import toast from 'react-hot-toast';
import { CheckCircle, XCircle, Eye, EyeOff } from 'lucide-react';

const Testimonials = () => {
    const [testimonials, setTestimonials] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all'); // all, approved, pending

    useEffect(() => {
        fetchTestimonials();
    }, [filter]);

    const fetchTestimonials = async () => {
        setLoading(true);
        try {
            const params = {};
            if (filter !== 'all') {
                params.status = filter;
            }
            const data = await testimonialService.getAllTestimonials(params);
            if (data.success && data.data && data.data.testimonials) {
                setTestimonials(data.data.testimonials);
            }
        } catch (error) {
            console.error('Error fetching testimonials:', error);
            toast.error('Failed to load testimonials');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateStatus = async (id, currentIsApproved, currentIsActive, field, newValue) => {
        try {
            const updateData = {};
            updateData[field] = newValue;

            const response = await testimonialService.updateTestimonial(id, updateData);
            if (response.success) {
                toast.success(`Testimonial updated successfully`);
                fetchTestimonials();
            }
        } catch (error) {
            console.error('Error updating testimonial:', error);
            toast.error('Failed to update testimonial');
        }
    };

    return (
        <div className="p-6 max-w-7xl mx-auto">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-900">Pandit Testimonials</h1>
                <div className="flex space-x-2">
                    <select
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        className="border-gray-300 rounded-md text-sm focus:ring-primary focus:border-primary"
                    >
                        <option value="all">All Testimonials</option>
                        <option value="approved">Approved</option>
                        <option value="pending">Pending</option>
                    </select>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
                {loading ? (
                    <div className="p-10 text-center text-gray-500">Loading testimonials...</div>
                ) : testimonials.length === 0 ? (
                    <div className="p-10 text-center text-gray-500">No testimonials found.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Pandit
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Rating
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Review
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {testimonials.map((testimonial) => (
                                    <tr key={testimonial.id}>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className="flex-shrink-0 h-10 w-10">
                                                    {testimonial.pandit?.profile?.image ? (
                                                        <img className="h-10 w-10 rounded-full object-cover" src={testimonial.pandit.profile.image} alt="" />
                                                    ) : (
                                                        <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                                                            <span className="text-gray-500 font-medium">
                                                                {testimonial.pandit?.profile?.name?.charAt(0) || 'P'}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="ml-4">
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {testimonial.pandit?.profile?.name || 'Unknown Pandit'}
                                                    </div>
                                                    <div className="text-sm text-gray-500">
                                                        {new Date(testimonial.createdAt).toLocaleDateString()}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex text-yellow-400">
                                                {[...Array(5)].map((_, i) => (
                                                    <StarIcon key={i} filled={i < testimonial.rating} />
                                                ))}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <p className="text-sm text-gray-900 line-clamp-3">{testimonial.reviewText}</p>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${testimonial.isApproved ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                                {testimonial.isApproved ? 'Approved' : 'Pending'}
                                            </span>
                                            {!testimonial.isActive && (
                                                <span className="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                                                    Hidden
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <div className="flex justify-end space-x-3">
                                                {testimonial.isApproved ? (
                                                    <button
                                                        onClick={() => handleUpdateStatus(testimonial.id, testimonial.isApproved, testimonial.isActive, 'isApproved', false)}
                                                        className="text-yellow-600 hover:text-yellow-900 flex items-center"
                                                        title="Revoke Approval"
                                                    >
                                                        <XCircle className="w-5 h-5" />
                                                    </button>
                                                ) : (
                                                    <button
                                                        onClick={() => handleUpdateStatus(testimonial.id, testimonial.isApproved, testimonial.isActive, 'isApproved', true)}
                                                        className="text-green-600 hover:text-green-900 flex items-center"
                                                        title="Approve"
                                                    >
                                                        <CheckCircle className="w-5 h-5" />
                                                    </button>
                                                )}

                                                {testimonial.isActive ? (
                                                    <button
                                                        onClick={() => handleUpdateStatus(testimonial.id, testimonial.isApproved, testimonial.isActive, 'isActive', false)}
                                                        className="text-gray-500 hover:text-gray-900 flex items-center"
                                                        title="Hide from public"
                                                    >
                                                        <EyeOff className="w-5 h-5" />
                                                    </button>
                                                ) : (
                                                    <button
                                                        onClick={() => handleUpdateStatus(testimonial.id, testimonial.isApproved, testimonial.isActive, 'isActive', true)}
                                                        className="text-blue-600 hover:text-blue-900 flex items-center"
                                                        title="Show to public"
                                                    >
                                                        <Eye className="w-5 h-5" />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

const StarIcon = ({ filled }) => (
    <svg className={`h-5 w-5 ${filled ? 'text-yellow-400' : 'text-gray-300'}`} fill="currentColor" viewBox="0 0 20 20">
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
    </svg>
);

export default Testimonials;
